import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { type SanityCombinedListingConfiguration } from '@data/sanity/queries/types/product'
import { usePrevious } from '@lib/hooks'
import { type Parameter, setParameter, useUrlParameters } from '@lib/parameters'
import { SiteContext } from '@lib/site-context'
import {
  addCombinedListingProductToParameter,
  clearCombinedListingProductTypeFromParameter,
  combinedListingDefaultParameters,
} from './parameter'
import {
  findCombinedListingProducts,
  getCombinedListingProductsIds,
  getCombinedListingVariants,
} from './product'
import {
  type CombinedListingParameterKey,
  type CombinedListingParameter,
  type CombinedListingProductType,
  type CombinedListingStateManager,
} from './types'

/**
 * Returns combined listing state manager that uses URL parameters.
 */
export const useUrlStateManager =
  (): CombinedListingStateManager<CombinedListingParameter> => {
    const { isRouteChanging } = useContext(SiteContext)

    const [urlParameters, setUrlParameters] = useUrlParameters(
      combinedListingDefaultParameters,
    )
    const previousUrlParameters = usePrevious(urlParameters)
    const activeUrlParameters = useMemo(
      () =>
        isRouteChanging && previousUrlParameters
          ? previousUrlParameters
          : urlParameters,
      [urlParameters, isRouteChanging, previousUrlParameters],
    )

    return {
      type: 'url',
      parameters: activeUrlParameters.map((urlParameter) => ({
        name: urlParameter.name as CombinedListingParameterKey,
        value:
          urlParameter.value && Array.isArray(urlParameter.value)
            ? urlParameter.value.join(',')
            : urlParameter.value,
      })),
      setParameters: setUrlParameters,
    }
  }

/**
 * Returns combined listing state manager that uses memory.
 */
export const useMemoryStateManager =
  (): CombinedListingStateManager<CombinedListingParameter> => {
    const [parameters, setParameters] = useState<CombinedListingParameter[]>(
      combinedListingDefaultParameters,
    )

    return {
      type: 'memory',
      parameters,
      setParameters,
    }
  }

/**
 * Combined listing product hero functionality hook.
 */
export const useCombinedListing = (
  stateManager: CombinedListingStateManager<CombinedListingParameter>,
  combinedListingMainProductId?: number,
  combinedListingConfiguration?: SanityCombinedListingConfiguration,
) => {
  const { parameters, setParameters } = stateManager

  const { isRouteChanging } = useContext(SiteContext)

  const parametersRef = useRef<Parameter<CombinedListingParameterKey>[]>([])
  const parametersLoadedRef = useRef(true)
  const mainProductParameterLoadedRef = useRef(true)

  // Active products, variants, size and passepartout cut-out
  const combinedListingProducts = useMemo(() => {
    const productsIds = getCombinedListingProductsIds(parameters)

    if (!productsIds || !combinedListingConfiguration) {
      return []
    }

    return findCombinedListingProducts(
      combinedListingConfiguration,
      productsIds,
    )
  }, [parameters, combinedListingConfiguration])

  const combinedListingFrameSize = useMemo(() => {
    const frameSizeParameter = parameters.find(
      (parameter) => parameter.name === 'frame_size',
    )

    return frameSizeParameter?.value
  }, [parameters])

  const combinedListingVariants = useMemo(() => {
    return getCombinedListingVariants(
      combinedListingProducts,
      combinedListingFrameSize ?? null,
    )
  }, [combinedListingProducts, combinedListingFrameSize])

  const combinedListingArtworkSize = useMemo(() => {
    const artworkSizeParameter = parameters.find(
      (parameter) => parameter.name === 'artwork_size',
    )

    return artworkSizeParameter?.value
  }, [parameters])

  /**
   * Adds a product ID to product parameter.
   */
  const addCombinedListingProduct = useCallback(
    (productId: number) => {
      if (!combinedListingConfiguration) {
        return
      }

      const newParameters = setParameter(
        parametersRef.current,
        'products',
        addCombinedListingProductToParameter(
          combinedListingConfiguration,
          parametersRef.current,
          productId,
        ),
      )
      parametersRef.current = newParameters
      setParameters(newParameters as CombinedListingParameter[])
    },
    [combinedListingConfiguration, setParameters],
  )

  /**
   * Sets size parameter value.
   */
  const setCombinedListingFrameSize = useCallback(
    (newFrameSize: string | null) => {
      const newParameters = setParameter(
        parametersRef.current,
        'frame_size',
        newFrameSize,
      )
      parametersRef.current = newParameters
      setParameters(newParameters as CombinedListingParameter[])
    },
    [setParameters],
  )

  /**
   * Sets passepartout cut-out parameter value.
   */
  const setCombinedListingArtworkSize = useCallback(
    (newArtworkSize: string | null) => {
      const newParameters = setParameter<CombinedListingParameterKey>(
        parametersRef.current,
        'artwork_size',
        newArtworkSize,
      )
      parametersRef.current = newParameters
      setParameters(newParameters as CombinedListingParameter[])
    },
    [setParameters],
  )

  /**
   * Removes product IDs from product parameter that match the specified type.
   */
  const clearCombinedListingProductType = useCallback(
    (type: CombinedListingProductType) => {
      if (!combinedListingConfiguration) {
        return
      }

      const newParameters = setParameter(
        parametersRef.current,
        'products',
        clearCombinedListingProductTypeFromParameter(
          combinedListingConfiguration,
          parametersRef.current,
          type,
        ),
      )
      parametersRef.current = newParameters
      setParameters(newParameters as CombinedListingParameter[])
    },
    [combinedListingConfiguration, setParameters],
  )

  /**
   * Clears product and size parameters.
   */
  const clearCombinedListing = useCallback(() => {
    const newParameters = [...combinedListingDefaultParameters]
    parametersRef.current = newParameters
    setParameters(newParameters)
  }, [setParameters])

  // Unset loaded state when product is changing
  useEffect(() => {
    if (combinedListingMainProductId && combinedListingConfiguration) {
      parametersLoadedRef.current = false
      mainProductParameterLoadedRef.current = false
    }
  }, [combinedListingMainProductId, combinedListingConfiguration])

  // Maintain an inner copy of URL parameters
  useEffect(() => {
    const productsParameter = parameters.find(
      (parameter) => parameter.name === 'products',
    )

    if (
      typeof productsParameter?.value === 'undefined' ||
      parametersLoadedRef.current
    ) {
      return
    }

    parametersRef.current = parameters
    parametersLoadedRef.current = true
  }, [parameters])

  // Add main product ID to product parameter
  useEffect(() => {
    // Wait for main product ID to be loaded and don't update after initial load
    if (
      isRouteChanging ||
      !combinedListingMainProductId ||
      mainProductParameterLoadedRef.current
    ) {
      return
    }

    const productsParameter = parameters.find(
      (parameter) => parameter.name === 'products',
    )

    if (typeof productsParameter?.value === 'undefined') {
      return
    }

    // Check if product ID isn't already in product parameter
    if (!productsParameter.value?.includes(`${combinedListingMainProductId}`)) {
      addCombinedListingProduct(combinedListingMainProductId)
    }

    mainProductParameterLoadedRef.current = true
  }, [
    addCombinedListingProduct,
    combinedListingMainProductId,
    isRouteChanging,
    parameters,
  ])

  return [
    combinedListingProducts,
    combinedListingVariants,
    combinedListingFrameSize,
    combinedListingArtworkSize,
    addCombinedListingProduct,
    setCombinedListingFrameSize,
    setCombinedListingArtworkSize,
    clearCombinedListingProductType,
    clearCombinedListing,
  ] as const
}
